import { useState, useMemo } from 'react';
import { useReportStore } from '../../store';
import { useCategories } from '../../hooks/useCategories';
import { useTopTransactions } from '../../hooks/useTopTransactions';
import { useIsMobile } from '../../hooks/useIsMobile';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { TransactionDetailModal } from '../ui/TransactionDetailModal';
import { formatDateShort, formatAmount } from '../../utils/format';
import type { Transaction } from '../../types';

const PAGE_SIZE = 10;
const MAX_TRANSACTIONS = 30;

export function TopTransactionsTable() {
  const { dateRange, selectedCategoryIds } = useReportStore();
  const categories = useCategories();
  const isMobile = useIsMobile();
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);

  const topTransactions = useTopTransactions({
    startDate: dateRange.start,
    endDate: dateRange.end,
    categoryIds:
      selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined,
    limit: MAX_TRANSACTIONS,
  });

  const {
    paginatedData: paginatedTransactions,
    currentPage,
    totalPages,
    goToPrevPage,
    goToNextPage,
  } = usePagination({ data: topTransactions, pageSize: PAGE_SIZE });

  const categoryMap = useMemo(() => {
    const map = new Map<string, { name: string; icon?: string }>();
    categories.forEach((c) => map.set(c.id, { name: c.name, icon: c.icon }));
    return map;
  }, [categories]);

  const handleRowClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
  };

  const handleCloseModal = () => {
    setSelectedTransaction(null);
  };

  if (topTransactions.length === 0) {
    return (
      <div className="text-center py-8 text-mid-gray">
        <p>No hay transacciones en el periodo seleccionado</p>
      </div>
    );
  }

  return (
    <>
      {isMobile ? (
        <div className="flex flex-col gap-3">
          {paginatedTransactions.map((transaction) => {
            const category = transaction.categoryId
              ? categoryMap.get(transaction.categoryId)
              : null;
            return (
              <button
                key={transaction.id}
                type="button"
                onClick={() => handleRowClick(transaction)}
                className="w-full text-left bg-near-white rounded-lg p-4 hover:bg-light-gray transition cursor-pointer"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm text-mid-gray">
                    {formatDateShort(transaction.date)}
                  </span>
                  <span className="font-bold text-near-black">
                    {formatAmount(transaction.amount)}
                  </span>
                </div>
                <p className="font-medium text-near-black mb-1 break-words">
                  {transaction.description}
                </p>
                {category && (
                  <p className="text-xs text-mid-gray">
                    {category.icon} {category.name}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="top-transactions-table">
            <thead className="bg-light-gray border-b border-mid-gray">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-near-black">
                  Fecha
                </th>
                <th className="text-left px-4 py-3 font-semibold text-near-black">
                  Descripcion
                </th>
                <th className="text-left px-4 py-3 font-semibold text-near-black">
                  Categoria
                </th>
                <th className="text-right px-4 py-3 font-semibold text-near-black">
                  Monto
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.map((transaction) => {
                const category = transaction.categoryId
                  ? categoryMap.get(transaction.categoryId)
                  : null;
                return (
                  <tr
                    key={transaction.id}
                    onClick={() => handleRowClick(transaction)}
                    className="border-b border-light-gray hover:bg-near-white cursor-pointer"
                  >
                    <td className="px-4 py-3 text-mid-gray whitespace-nowrap">
                      {formatDateShort(transaction.date)}
                    </td>
                    <td className="px-4 py-3 text-near-black max-w-xs truncate">
                      {transaction.description}
                    </td>
                    <td className="px-4 py-3 text-near-black whitespace-nowrap">
                      {category ? (
                        <span>
                          {category.icon} {category.name}
                        </span>
                      ) : (
                        <span className="text-mid-gray">Sin categoria</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-near-black whitespace-nowrap">
                      {formatAmount(transaction.amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPrevPage={goToPrevPage}
        onNextPage={goToNextPage}
      />

      <TransactionDetailModal
        transaction={selectedTransaction}
        isOpen={selectedTransaction !== null}
        onClose={handleCloseModal}
      />
    </>
  );
}
